import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RequireWrite, TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import {
  createFund,
  type FundStatus,
  type FundStrategy,
  type FundStructure,
} from "@/lib/api/funds";

const STRUCTURES: FundStructure[] = [
  "limited_partnership",
  "llc",
  "trust",
  "plc",
  "sicav",
  "other",
];
const STRATEGIES: FundStrategy[] = [
  "buyout",
  "growth",
  "venture",
  "credit",
  "real_estate",
  "infrastructure",
  "fund_of_funds",
  "other",
];
const STATUSES: FundStatus[] = ["forming", "fundraising", "active", "harvesting", "wound_down"];

function FundsNewInner() {
  const tenant = useTenant();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [vintage, setVintage] = useState("");
  const [sizeUsd, setSizeUsd] = useState("");
  const [domicile, setDomicile] = useState("");
  const [structure, setStructure] = useState<FundStructure | "">("");
  const [strategy, setStrategy] = useState<FundStrategy | "">("");
  const [status, setStatus] = useState<FundStatus>("forming");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: createFund,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["funds", tenant.id] });
      await navigate({ to: "/t/$slug/funds", params: { slug: tenant.slug } });
    },
    onError: (err: Error) => setError(err.message),
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      tenant_id: tenant.id,
      name,
      short_name: shortName || null,
      vintage_year: vintage ? Number(vintage) : null,
      fund_size_usd: sizeUsd ? Number(sizeUsd) : null,
      domicile: domicile || null,
      structure: structure || null,
      strategy: strategy || null,
      status,
    });
  }

  return (
    <>
      <header className="space-y-1">
        <Link
          to="/t/$slug/funds"
          params={{ slug: tenant.slug }}
          className="text-xs text-neutral-500 hover:text-neutral-300"
        >
          ← Back to funds
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">New fund</h1>
      </header>

      <form onSubmit={onSubmit} className="max-w-2xl space-y-4">
        <Field label="Name" required>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Short name">
            <input
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Vintage year">
            <input
              type="number"
              min={1900}
              max={2200}
              value={vintage}
              onChange={(e) => setVintage(e.target.value)}
              className="input"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Fund size (USD)">
            <input
              type="number"
              min={0}
              step={1000000}
              value={sizeUsd}
              onChange={(e) => setSizeUsd(e.target.value)}
              className="input"
              placeholder="500000000"
            />
          </Field>
          <Field label="Domicile">
            <input
              value={domicile}
              onChange={(e) => setDomicile(e.target.value)}
              className="input"
              placeholder="Delaware, Cayman, …"
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Structure">
            <select
              value={structure}
              onChange={(e) => setStructure(e.target.value as FundStructure | "")}
              className="input"
            >
              <option value="">—</option>
              {STRUCTURES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Strategy">
            <select
              value={strategy}
              onChange={(e) => setStrategy(e.target.value as FundStrategy | "")}
              className="input"
            >
              <option value="">—</option>
              {STRATEGIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as FundStatus)}
              className="input"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {error ? (
          <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        ) : null}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-950 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending ? "Creating…" : "Create fund"}
          </button>
          <Link
            to="/t/$slug/funds"
            params={{ slug: tenant.slug }}
            className="rounded border border-neutral-800 px-4 py-2 text-sm text-neutral-300 hover:bg-neutral-900"
          >
            Cancel
          </Link>
        </div>
      </form>
    </>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}

function Field({ label, required, children }: FieldProps) {
  return (
    <label className="block space-y-1">
      <span className="text-xs uppercase tracking-wide text-neutral-400">
        {label}
        {required ? <span className="ml-0.5 text-red-400">*</span> : null}
      </span>
      {children}
    </label>
  );
}

export function FundsNewPage() {
  return (
    <TenantLayout>
      <RequireWrite>
        <FundsNewInner />
      </RequireWrite>
    </TenantLayout>
  );
}
